import type {
  ActivityFeedResponse,
  CarePlanResponse,
  CareTeamMember,
  GenerateOtcResponse,
  GetPresignedUrlForGetPayload,
  GetPresignedUrlForGetResponse,
  GetPresignedUrlForPostPayload,
  GetPresignedUrlForPostResponse,
  Note,
  PatientDetailsResponse,
  PatientDiagnosis,
} from "@/core/types";
import type { AptiRequestConfig } from "@/lib/types/api";
import { buildRequestURLWithQueryParamObject } from "@/utils/requestUtil";
import { getGroupAuthType } from "@/redux/actions/auth";
import { GROUP_AUTH_HEADER_NAME } from "@/constants/axios";
import { httpAuth } from "@/utils/axios";
import type { PatientFile } from "@/features/patient/types/PatientFile";

export const sharedRequestsTyped = {
  fetch_patient_careteam: function ({
    params,
    patientId,
    queryParams = {},
  }: {
    params?: Record<string, unknown>;
    patientId: string;
    queryParams?: Record<string, string>;
  }): Promise<CareTeamMember[]> {
    const config = {
      headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      method: "POST",
      params,
      url: buildRequestURLWithQueryParamObject(
        `/v1/patient_management/${patientId}/care_team`,
        queryParams,
      ),
    } satisfies AptiRequestConfig;

    return httpAuth(config) as unknown as Promise<CareTeamMember[]>;
  },

  fetch_patient_details: function ({
    params,
    patientId,
  }: {
    params?: Record<string, unknown>;
    patientId: string;
  }): Promise<PatientDetailsResponse> {
    const config = {
      headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      method: "POST",
      params,
      url: `/v1/patient_management/${patientId}/patient_details`,
    } satisfies AptiRequestConfig;

    return httpAuth(config) as unknown as Promise<PatientDetailsResponse>;
  },

  generate_otc: function ({
    params,
    patientId,
  }: {
    params?: Record<string, unknown>;
    patientId: string;
  }): Promise<GenerateOtcResponse> {
    const config = {
      headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      method: "POST",
      params,
      url: `/v1/auth/generate_otc?patient_id=${patientId}`,
    } satisfies AptiRequestConfig;

    return httpAuth(config) as unknown as Promise<GenerateOtcResponse>;
  },

  get_activity_feed: function ({
    params,
    patientId,
  }: {
    params?: Record<string, unknown>;
    patientId: string;
  }): Promise<ActivityFeedResponse> {
    const config = {
      headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      method: "POST",
      params,
      url: `/v1/patient_management/${patientId}/activity`,
    } satisfies AptiRequestConfig;

    return httpAuth(config) as unknown as Promise<ActivityFeedResponse>;
  },

  get_care_plan: function ({
    params,
    patientId,
  }: {
    params?: Record<string, unknown>;
    patientId: string;
  }): Promise<CarePlanResponse> {
    const config = {
      headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      method: "POST",
      params,
      url: `/v1/care-plan/${patientId}/get_care_plan`,
    } satisfies AptiRequestConfig;

    return httpAuth(config) as unknown as Promise<CarePlanResponse>;
  },

  get_files: function ({
    data,
  }: {
    data: { entity_id: string; entity_type: string };
  }): Promise<PatientFile[]> {
    const config = {
      data,
      headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      method: "POST",
      params: { showLoader: false },
      url: "/v1/provider/get_files",
    } satisfies AptiRequestConfig<{ entity_id: string; entity_type: string }>;

    return httpAuth(config) as unknown as Promise<PatientFile[]>;
  },

  get_notes_summary: function ({
    params,
    patientId,
  }: {
    params?: Record<string, unknown>;
    patientId: string;
  }): Promise<Note[]> {
    const config = {
      headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      method: "POST",
      params,
      url: `/v1/patient_management/${patientId}/get_notes_summary`,
    } satisfies AptiRequestConfig;

    return httpAuth(config) as unknown as Promise<Note[]>;
  },

  get_patient_diagnosis: function ({
    params,
    patientId,
  }: {
    params?: Record<string, unknown>;
    patientId: string;
  }): Promise<PatientDiagnosis> {
    const config = {
      headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      method: "POST",
      params,
      url: `/v1/patient_management/${patientId}/get_patient_diagnosis`,
    } satisfies AptiRequestConfig;

    return httpAuth(config) as unknown as Promise<PatientDiagnosis>;
  },

  get_presigned_url: function ({
    data,
    version = "v4",
  }: {
    data: GetPresignedUrlForGetPayload;
    version?: string;
  }): Promise<GetPresignedUrlForGetResponse> {
    const config = {
      data,
      headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      method: "POST",
      params: { showLoader: false },
      url: `/${version}/provider/get_presigned_url`,
    } satisfies AptiRequestConfig<GetPresignedUrlForGetPayload>;

    return httpAuth(config) as unknown as Promise<GetPresignedUrlForGetResponse>;
  },

  post_presigned_url: function ({
    data,
    version = "v4",
  }: {
    data: GetPresignedUrlForPostPayload;
    version?: string;
  }): Promise<GetPresignedUrlForPostResponse> {
    const config = {
      data,
      headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      method: "POST",
      params: { showLoader: false },
      url: `/${version}/provider/get_presigned_url`,
    } satisfies AptiRequestConfig<GetPresignedUrlForPostPayload>;

    return httpAuth(config) as unknown as Promise<GetPresignedUrlForPostResponse>;
  },

  send_on_demand_welcome_email: function ({
    params,
    patientId,
  }: {
    params?: Record<string, unknown>;
    patientId: string;
  }): Promise<void> {
    const config = {
      headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      method: "POST",
      params,
      url: `/v1/patient_management/${patientId}/send_on_demand_welcome_email`,
    } satisfies AptiRequestConfig;

    return httpAuth(config) as unknown as Promise<void>;
  },
};
